import { atom, selector } from "recoil"

import type {
  AllBrochureFieldsFragment,
  BrochureUtilityBillFieldsFragment,
} from "~/graphql/client"
import { parseEscalatorRate } from "~/utils/finance/format"

import { dialYearState } from "../../Brochure/MonthlyBillSlide/MonthlyBillSlide"
import { selectedLoanProductState } from "../../Setup/Form/Steps/FinancingSelection/state"
import { isTpoProductSelectedState } from "../loan-products/tpo-loan-product"
import { safeQuoteRemoteState } from "../quotes/quotes-state"

const DEFAULT_BROCHURE: AllBrochureFieldsFragment = {
  id: ``,
  afterSolarUtilityBills: [],
  beforeSolarUtilityBills: [],
  months: [],
  proposalId: ``,
  annualSolarProduction: 0,
  createdAt: ``,
  updatedAt: ``,
  dialUtilityRates: [],
  utilityRateEscalationPercentage: 0,
  energyOffset: 0,
  userAdjustedUtilityRate: 0,
  baseGenabilityUtilityRate: 0,
}

const sortBySortOrder = (a: { sortOrder: number }, b: { sortOrder: number }) =>
  a.sortOrder - b.sortOrder

export const brochureState = atom<AllBrochureFieldsFragment>({
  key: `brochure`,
  default: DEFAULT_BROCHURE,
})

const afterSolarUtilityBillsState = selector<
  BrochureUtilityBillFieldsFragment[]
>({
  key: `afterSolarUtilityBills`,
  get: ({ get }) => {
    const brochure = get(brochureState)

    return [...brochure.afterSolarUtilityBills].sort(sortBySortOrder)
  },
})

const beforeSolarUtilityBillsState = selector<
  BrochureUtilityBillFieldsFragment[]
>({
  key: `beforeSolarUtilityBills`,
  get: ({ get }) => {
    const brochure = get(brochureState)

    return [...brochure.beforeSolarUtilityBills].sort(sortBySortOrder)
  },
})

export const formatEnergyOffset = (energyOffset: number): string =>
  Intl.NumberFormat(`en-US`, {
    maximumFractionDigits: 0,
    style: `percent`,
  }).format(energyOffset)

export const offsetAsPercentage = selector<string>({
  key: `offsetAsPercentage`,
  get: ({ get }) => formatEnergyOffset(get(brochureState).energyOffset),
})

export const currentBeforeSolarUtilityBillState =
  selector<BrochureUtilityBillFieldsFragment>({
    key: `currentBeforeSolarUtilityBill`,
    get: ({ get }) => {
      const beforeSolarUtilityBills = get(beforeSolarUtilityBillsState)
      const { years } = get(dialYearState)

      return beforeSolarUtilityBills[years - 1]
    },
  })

export const calculateTpoMonthlyPayment = ({
  monthlyInitialPayment,
  escalatorRate,
  year,
}: {
  monthlyInitialPayment: number
  escalatorRate: number
  year: number
}): number => {
  const escalatorRateAsPercent = escalatorRate / 100

  return monthlyInitialPayment * Math.pow(1 + escalatorRateAsPercent, year - 1)
}

export const currentAfterSolarUtilityBillState =
  selector<BrochureUtilityBillFieldsFragment>({
    key: `currentAfterSolarUtilityBill`,
    get: ({ get }) => {
      const isTpo = get(isTpoProductSelectedState)
      const { years } = get(dialYearState)
      const afterSolarUtilityBills = get(afterSolarUtilityBillsState)
      const billForYear = afterSolarUtilityBills[years - 1]

      if (!isTpo) {
        return billForYear
      }

      const quote = get(safeQuoteRemoteState)
      const escalatorRate =
        parseEscalatorRate(
          get(selectedLoanProductState)?.providerMeta?.escalatorRate,
        ) ?? 0
      const monthlyPayments = Array.from({ length: 25 }).map((_, i) =>
        calculateTpoMonthlyPayment({
          monthlyInitialPayment: quote?.monthlyInitialPayment ?? 0,
          escalatorRate,
          year: i + 1,
        }),
      )

      return {
        ...billForYear,
        payment: monthlyPayments[years - 1],
      }
    },
  })
